import { CurrencyAmount, JSBI, Token, Trade } from '@pancakeswap-libs/sdk'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ArrowDown } from 'react-feather'
import { CardBody, ArrowDownIcon, Button, IconButton, Text } from '@pancakeswap-libs/uikit'
import { ThemeContext } from 'styled-components'
import AddressInputPanel from 'components/AddressInputPanel'
import Card, { GreyCard } from 'components/Card'
import { AutoColumn } from 'components/Column'
import ConfirmSwapModal from 'components/swap/ConfirmSwapModal'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import CardNav from 'components/CardNav'
import { AutoRow, RowBetween } from 'components/Row'
import AdvancedSwapDetailsDropdown from 'components/swap/AdvancedSwapDetailsDropdown'
import confirmPriceImpactWithoutFee from 'components/swap/confirmPriceImpactWithoutFee'
import { ArrowWrapper, BottomGrouping, SwapCallbackError, Wrapper } from 'components/swap/styleds'
import TradePrice from 'components/swap/TradePrice'
import TokenWarningModal from 'components/TokenWarningModal'
import SyrupWarningModal from 'components/SyrupWarningModal'
import ProgressSteps from 'components/ProgressSteps'

import { INITIAL_ALLOWED_SLIPPAGE } from 'constants/index'
import { useActiveWeb3React } from 'hooks'
import { useCurrency } from 'hooks/Tokens'
import { ApprovalState, useApproveCallback, useApproveCallbackFromTrade } from 'hooks/useApproveCallback'
import { useSwapCallback } from 'hooks/useSwapCallback'
import useWrapCallback, { WrapType } from 'hooks/useWrapCallback'
import { Field } from 'state/swap/actions'
import { useDefaultsFromURLSearch, useDerivedSwapInfo, useSwapActionHandlers, useSwapState } from 'state/swap/hooks'
import { useExpertModeManager, useUserDeadline, useUserSlippageTolerance } from 'state/user/hooks'
import { LinkStyledButton } from 'components/Shared'
import { maxAmountSpend } from 'utils/maxAmountSpend'
import { computeTradePriceBreakdown, warningSeverity } from 'utils/prices'
import Loader from 'components/Loader'
import useI18n from 'hooks/useI18n'
import PageHeader from 'components/PageHeader'
import ConnectWalletButton from 'components/ConnectWalletButton'
import useSwitchNetwork, { useIsWrongNetwork } from 'hooks/useSwitchNetwork'
import useAggregatorTrade from 'hooks/useAggregatorTrade'
import useAggregatorSwapCallback from 'hooks/useAggregatorSwapCallback'
import AggregatorRoute from 'components/swap/AggregatorRoute'
import AggregatorConfirmModal from 'components/swap/AggregatorConfirmModal'
import { requiresImpactConfirmation, severityFromBps } from 'utils/aggregatorPrices'
import { getChainConfig } from 'constants/chains'
import { NETWORK_CHAIN_ID } from 'connectors'
import AppBody from '../AppBody'

const Swap = () => {
  const loadedUrlParams = useDefaultsFromURLSearch()
  const TranslateString = useI18n()

  // token warning stuff
  const [loadedInputCurrency, loadedOutputCurrency] = [
    useCurrency(loadedUrlParams?.inputCurrencyId),
    useCurrency(loadedUrlParams?.outputCurrencyId),
  ]
  const [dismissTokenWarning, setDismissTokenWarning] = useState<boolean>(false)
  const [isSyrup, setIsSyrup] = useState<boolean>(false)
  const [syrupTransactionType, setSyrupTransactionType] = useState<string>('')
  const urlLoadedTokens: Token[] = useMemo(
    () => [loadedInputCurrency, loadedOutputCurrency]?.filter((c): c is Token => c instanceof Token) ?? [],
    [loadedInputCurrency, loadedOutputCurrency]
  )
  const handleConfirmTokenWarning = useCallback(() => {
    setDismissTokenWarning(true)
  }, [])

  const handleConfirmSyrupWarning = useCallback(() => {
    setIsSyrup(false)
    setSyrupTransactionType('')
  }, [])

  const { account } = useActiveWeb3React()
  const theme = useContext(ThemeContext)

  const isWrongNetwork = useIsWrongNetwork()
  const { switchNetwork, switching } = useSwitchNetwork()
  const expectedChainName = getChainConfig(NETWORK_CHAIN_ID)?.name ?? `chain ${NETWORK_CHAIN_ID}`

  const [isExpertMode] = useExpertModeManager()

  // get custom setting values for user
  const [deadline] = useUserDeadline()
  const [allowedSlippage] = useUserSlippageTolerance()

  // swap state
  const { independentField, typedValue, recipient } = useSwapState()
  const { v2Trade, currencyBalances, parsedAmount, currencies, inputError: swapInputError } = useDerivedSwapInfo()
  const { wrapType, execute: onWrap, inputError: wrapInputError } = useWrapCallback(
    currencies[Field.INPUT],
    currencies[Field.OUTPUT],
    typedValue
  )
  const showWrap: boolean = wrapType !== WrapType.NOT_APPLICABLE
  const trade = showWrap ? undefined : v2Trade

  // Aggregator quotes are exact-in only, so only the sell amount can drive one.
  const aggregatorInputAmount = independentField === Field.INPUT ? parsedAmount : undefined
  const aggregatorTrade = useAggregatorTrade(
    currencies[Field.INPUT],
    currencies[Field.OUTPUT],
    aggregatorInputAmount,
    allowedSlippage
  )

  // Falls back to the on-chain router wherever no source can quote the chain, which
  // is what keeps the app working on testnet while OpenOcean serves mainnet only.
  const useAggregator = aggregatorTrade.active && !showWrap
  const aggregatorExactOutUnsupported = useAggregator && independentField === Field.OUTPUT && Boolean(parsedAmount)

  let parsedAmounts
  if (showWrap) {
    parsedAmounts = { [Field.INPUT]: parsedAmount, [Field.OUTPUT]: parsedAmount }
  } else if (useAggregator) {
    parsedAmounts = {
      [Field.INPUT]: aggregatorInputAmount,
      [Field.OUTPUT]: aggregatorTrade.outputAmount,
    }
  } else {
    parsedAmounts = {
      [Field.INPUT]: independentField === Field.INPUT ? parsedAmount : trade?.inputAmount,
      [Field.OUTPUT]: independentField === Field.OUTPUT ? parsedAmount : trade?.outputAmount,
    }
  }

  const { onSwitchTokens, onCurrencySelection, onUserInput, onChangeRecipient } = useSwapActionHandlers()
  const isValid = !swapInputError
  const dependentField: Field = independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT

  const handleTypeInput = useCallback(
    (value: string) => {
      onUserInput(Field.INPUT, value)
    },
    [onUserInput]
  )
  const handleTypeOutput = useCallback(
    (value: string) => {
      onUserInput(Field.OUTPUT, value)
    },
    [onUserInput]
  )

  // modal and loading
  const [{ showConfirm, tradeToConfirm, swapErrorMessage, attemptingTxn, txHash }, setSwapState] = useState<{
    showConfirm: boolean
    tradeToConfirm: Trade | undefined
    attemptingTxn: boolean
    swapErrorMessage: string | undefined
    txHash: string | undefined
  }>({
    showConfirm: false,
    tradeToConfirm: undefined,
    attemptingTxn: false,
    swapErrorMessage: undefined,
    txHash: undefined,
  })

  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: showWrap
      ? parsedAmounts[independentField]?.toExact() ?? ''
      : parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const route = trade?.route
  const userHasSpecifiedInputOutput = Boolean(
    currencies[Field.INPUT] && currencies[Field.OUTPUT] && parsedAmounts[independentField]?.greaterThan(JSBI.BigInt(0))
  )
  // While a quote is in flight there is no route *yet*, which is not the same as
  // there being no liquidity - showing "insufficient liquidity" mid-fetch is wrong.
  const noRoute = useAggregator ? !aggregatorTrade.quote && !aggregatorTrade.loading : !route

  // check whether the user has approved the router on the input token
  const [routerApproval, routerApproveCallback] = useApproveCallbackFromTrade(trade, allowedSlippage)

  // The aggregator's spender comes from the quote, not from a fixed router address -
  // each source settles through its own contract, and approving a hardcoded one
  // sends the allowance somewhere that cannot execute the trade.
  const [aggregatorApproval, aggregatorApproveCallback] = useApproveCallback(
    useAggregator ? parsedAmounts[Field.INPUT] : undefined,
    useAggregator ? aggregatorTrade.allowanceTarget : undefined
  )

  const approval = useAggregator ? aggregatorApproval : routerApproval
  const approveCallback = useAggregator ? aggregatorApproveCallback : routerApproveCallback

  // check if user has gone through approval process, used to show two step buttons, reset on token change
  const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)

  // mark when a user has submitted an approval, reset onTokenSelection for input field
  useEffect(() => {
    if (approval === ApprovalState.PENDING) {
      setApprovalSubmitted(true)
    }
  }, [approval, approvalSubmitted])

  // The quote the user actually agreed to. Quotes keep refreshing behind the modal,
  // so signing must be gated on the user having seen the current one.
  const [acceptedQuoteOutput, setAcceptedQuoteOutput] = useState<string | undefined>(undefined)
  const currentQuoteOutput = aggregatorTrade.quote?.buyAmount

  const quotePriceUpdated = Boolean(
    showConfirm && acceptedQuoteOutput && currentQuoteOutput && currentQuoteOutput !== acceptedQuoteOutput
  )

  const handleAcceptQuote = useCallback(() => {
    setAcceptedQuoteOutput(currentQuoteOutput)
  }, [currentQuoteOutput])

  const maxAmountInput: CurrencyAmount | undefined = maxAmountSpend(currencyBalances[Field.INPUT])
  const atMaxAmountInput = Boolean(maxAmountInput && parsedAmounts[Field.INPUT]?.equalTo(maxAmountInput))

  // the callback to execute the swap
  const { callback: routerSwapCallback, error: routerSwapCallbackError } = useSwapCallback(
    trade,
    allowedSlippage,
    deadline,
    recipient
  )

  const { callback: aggregatorSwapCallback, error: aggregatorSwapCallbackError } = useAggregatorSwapCallback(
    useAggregator ? aggregatorTrade.quote : null,
    parsedAmounts[Field.INPUT],
    currencies[Field.INPUT],
    currencies[Field.OUTPUT]
  )

  const swapCallback = useAggregator ? aggregatorSwapCallback : routerSwapCallback
  const swapCallbackError = useAggregator ? aggregatorSwapCallbackError : routerSwapCallbackError

  const { priceImpactWithoutFee } = computeTradePriceBreakdown(trade)

  const handleSwap = useCallback(() => {
    // expert mode users have already acknowledged the price-impact risk in the settings
    // modal, so the extra native prompt/confirm gate is skipped for them
    if (useAggregator) {
      if (requiresImpactConfirmation(aggregatorTrade.priceImpactBps, isExpertMode)) {
        // eslint-disable-next-line no-alert
        if (!window.confirm('This trade has a very high price impact. Swap anyway?')) return
      }
    } else if (!isExpertMode && priceImpactWithoutFee && !confirmPriceImpactWithoutFee(priceImpactWithoutFee)) {
      return
    }
    if (!swapCallback) {
      return
    }
    if (quotePriceUpdated) {
      return
    }
    setSwapState((prevState) => ({ ...prevState, attemptingTxn: true, swapErrorMessage: undefined, txHash: undefined }))
    swapCallback()
      .then((hash) => {
        setSwapState((prevState) => ({
          ...prevState,
          attemptingTxn: false,
          swapErrorMessage: undefined,
          txHash: hash,
        }))
      })
      .catch((error) => {
        setSwapState((prevState) => ({
          ...prevState,
          attemptingTxn: false,
          swapErrorMessage: error.message,
          txHash: undefined,
        }))
      })
  }, [
    priceImpactWithoutFee,
    swapCallback,
    setSwapState,
    isExpertMode,
    useAggregator,
    aggregatorTrade.priceImpactBps,
    quotePriceUpdated,
  ])

  // errors
  const [showInverted, setShowInverted] = useState<boolean>(false)

  // warnings on slippage
  const priceImpactSeverity = useAggregator
    ? severityFromBps(aggregatorTrade.priceImpactBps)
    : warningSeverity(priceImpactWithoutFee)

  // show approve flow when: no error on inputs, not approved or pending, or approved in current session
  // never show if price impact is above threshold in non expert mode
  const showApproveFlow =
    !swapInputError &&
    (approval === ApprovalState.NOT_APPROVED ||
      approval === ApprovalState.PENDING ||
      (approvalSubmitted && approval === ApprovalState.APPROVED)) &&
    !(priceImpactSeverity > 3 && !isExpertMode)

  const handleConfirmDismiss = useCallback(() => {
    setSwapState((prevState) => ({ ...prevState, showConfirm: false }))

    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onUserInput(Field.INPUT, '')
    }
  }, [onUserInput, txHash, setSwapState])

  const handleAcceptChanges = useCallback(() => {
    setSwapState((prevState) => ({ ...prevState, tradeToConfirm: trade }))
  }, [trade])

  // This will check to see if the user has selected Syrup to either buy or sell.
  // If so, they will be alerted with a warning message.
  const checkForSyrup = useCallback(
    (selected: string, purchaseType: string) => {
      if (selected === 'syrup') {
        setIsSyrup(true)
        setSyrupTransactionType(purchaseType)
      }
    },
    [setIsSyrup, setSyrupTransactionType]
  )

  const handleInputSelect = useCallback(
    (inputCurrency) => {
      setApprovalSubmitted(false) // reset 2 step UI for approvals
      onCurrencySelection(Field.INPUT, inputCurrency)
      if (inputCurrency.symbol.toLowerCase() === 'syrup') {
        checkForSyrup(inputCurrency.symbol.toLowerCase(), 'Selling')
      }
    },
    [onCurrencySelection, setApprovalSubmitted, checkForSyrup]
  )

  const handleMaxInput = useCallback(() => {
    if (maxAmountInput) {
      onUserInput(Field.INPUT, maxAmountInput.toExact())
    }
  }, [maxAmountInput, onUserInput])

  const handleOutputSelect = useCallback(
    (outputCurrency) => {
      onCurrencySelection(Field.OUTPUT, outputCurrency)
      if (outputCurrency.symbol.toLowerCase() === 'syrup') {
        checkForSyrup(outputCurrency.symbol.toLowerCase(), 'Buying')
      }
    },
    [onCurrencySelection, checkForSyrup]
  )

  return (
    <>
      <TokenWarningModal
        isOpen={urlLoadedTokens.length > 0 && !dismissTokenWarning}
        tokens={urlLoadedTokens}
        onConfirm={handleConfirmTokenWarning}
      />
      <SyrupWarningModal
        isOpen={isSyrup}
        transactionType={syrupTransactionType}
        onConfirm={handleConfirmSyrupWarning}
      />
      <CardNav />
      <AppBody>
        <Wrapper id="swap-page">
          {useAggregator ? (
            <AggregatorConfirmModal
              isOpen={showConfirm}
              onDismiss={handleConfirmDismiss}
              onConfirm={handleSwap}
              attemptingTxn={attemptingTxn}
              txHash={txHash}
              quote={aggregatorTrade.quote}
              inputAmount={parsedAmounts[Field.INPUT]}
              outputAmount={aggregatorTrade.outputAmount}
              minimumReceived={aggregatorTrade.minimumReceived}
              inputCurrency={currencies[Field.INPUT]}
              outputCurrency={currencies[Field.OUTPUT]}
              priceUpdated={quotePriceUpdated}
              onAcceptPrice={handleAcceptQuote}
            />
          ) : (
            <ConfirmSwapModal
              isOpen={showConfirm}
              trade={trade}
              originalTrade={tradeToConfirm}
              onAcceptChanges={handleAcceptChanges}
              attemptingTxn={attemptingTxn}
              txHash={txHash}
              recipient={recipient}
              allowedSlippage={allowedSlippage}
              onConfirm={handleSwap}
              swapErrorMessage={swapErrorMessage}
              onDismiss={handleConfirmDismiss}
            />
          )}
          <PageHeader
            title={TranslateString(8, 'Exchange')}
            description={TranslateString(1192, 'Trade tokens in an instant')}
          />
          <CardBody>
            <AutoColumn gap="md">
              <CurrencyInputPanel
                label={
                  independentField === Field.OUTPUT && !showWrap && trade
                    ? TranslateString(194, 'From (estimated)')
                    : TranslateString(76, 'From')
                }
                value={formattedAmounts[Field.INPUT]}
                showMaxButton={!atMaxAmountInput}
                currency={currencies[Field.INPUT]}
                onUserInput={handleTypeInput}
                onMax={handleMaxInput}
                onCurrencySelect={handleInputSelect}
                otherCurrency={currencies[Field.OUTPUT]}
                id="swap-currency-input"
              />
              <AutoColumn justify="space-between">
                <AutoRow justify={isExpertMode ? 'space-between' : 'center'} style={{ padding: '0 1rem' }}>
                  <ArrowWrapper clickable>
                    <IconButton
                      variant="tertiary"
                      onClick={() => {
                        setApprovalSubmitted(false) // reset 2 step UI for approvals
                        onSwitchTokens()
                      }}
                      style={{ borderRadius: '50%' }}
                      scale="sm"
                      aria-label="Switch input and output tokens"
                    >
                      <ArrowDownIcon color="primary" width="24px" />
                    </IconButton>
                  </ArrowWrapper>
                  {recipient === null && !showWrap && isExpertMode ? (
                    <LinkStyledButton id="add-recipient-button" onClick={() => onChangeRecipient('')}>
                      + Add a send (optional)
                    </LinkStyledButton>
                  ) : null}
                </AutoRow>
              </AutoColumn>
              <CurrencyInputPanel
                value={formattedAmounts[Field.OUTPUT]}
                onUserInput={handleTypeOutput}
                label={
                  independentField === Field.INPUT && !showWrap && trade
                    ? TranslateString(196, 'To (estimated)')
                    : TranslateString(80, 'To')
                }
                showMaxButton={false}
                currency={currencies[Field.OUTPUT]}
                onCurrencySelect={handleOutputSelect}
                otherCurrency={currencies[Field.INPUT]}
                id="swap-currency-output"
              />

              {recipient !== null && !showWrap ? (
                <>
                  <AutoRow justify="space-between" style={{ padding: '0 1rem' }}>
                    <ArrowWrapper clickable={false}>
                      <ArrowDown size="16" color={theme.colors.textSubtle} />
                    </ArrowWrapper>
                    <LinkStyledButton id="remove-recipient-button" onClick={() => onChangeRecipient(null)}>
                      - Remove send
                    </LinkStyledButton>
                  </AutoRow>
                  <AddressInputPanel id="recipient" value={recipient} onChange={onChangeRecipient} />
                </>
              ) : null}

              {showWrap ? null : (
                <Card padding=".25rem .75rem 0 .75rem" borderRadius="20px">
                  <AutoColumn gap="4px">
                    {useAggregator && aggregatorTrade.quote && (
                      <>
                        <RowBetween align="center">
                          <Text fontSize="14px">{TranslateString(1210, 'Minimum received')}</Text>
                          <Text fontSize="14px">
                            {aggregatorTrade.minimumReceived?.toSignificant(6) ?? '-'}{' '}
                            {currencies[Field.OUTPUT]?.symbol}
                          </Text>
                        </RowBetween>
                        {aggregatorTrade.priceImpactBps !== undefined && (
                          <RowBetween align="center">
                            <Text fontSize="14px">{TranslateString(226, 'Price Impact')}</Text>
                            <Text
                              fontSize="14px"
                              color={
                                priceImpactSeverity > 2 ? 'failure' : priceImpactSeverity > 1 ? 'warning' : 'text'
                              }
                            >
                              {(Math.abs(aggregatorTrade.priceImpactBps) / 100).toFixed(2)}%
                            </Text>
                          </RowBetween>
                        )}
                        <AggregatorRoute
                          route={aggregatorTrade.route}
                          providerLabel={aggregatorTrade.quote.providerLabel}
                          improvementBps={aggregatorTrade.quote.improvementBps}
                        />
                      </>
                    )}
                    {!useAggregator && Boolean(trade) && (
                      <RowBetween align="center">
                        <Text fontSize="14px">{TranslateString(1182, 'Price')}</Text>
                        <TradePrice
                          price={trade?.executionPrice}
                          showInverted={showInverted}
                          setShowInverted={setShowInverted}
                        />
                      </RowBetween>
                    )}
                    {allowedSlippage !== INITIAL_ALLOWED_SLIPPAGE && (
                      <RowBetween align="center">
                        <Text fontSize="14px">{TranslateString(88, 'Slippage Tolerance')}</Text>
                        <Text fontSize="14px">{allowedSlippage / 100}%</Text>
                      </RowBetween>
                    )}
                  </AutoColumn>
                </Card>
              )}
            </AutoColumn>
            <BottomGrouping>
              {!account ? (
                <ConnectWalletButton width="100%" />
              ) : isWrongNetwork ? (
                // Router, multicall and token-list addresses are all chain-specific.
                // Trading on a chain the app is not configured for silently produces
                // wrong prices and failed transactions, so it is blocked outright.
                <Button width="100%" onClick={switchNetwork} disabled={switching}>
                  {switching
                    ? TranslateString(1198, 'Check your wallet...')
                    : `${TranslateString(1196, 'Switch to')} ${expectedChainName}`}
                </Button>
              ) : showWrap ? (
                <Button disabled={Boolean(wrapInputError)} onClick={onWrap} width="100%">
                  {wrapInputError ??
                    (wrapType === WrapType.WRAP ? 'Wrap' : wrapType === WrapType.UNWRAP ? 'Unwrap' : null)}
                </Button>
              ) : aggregatorExactOutUnsupported ? (
                // Every aggregator in use quotes sell-amount-in only, so driving the
                // trade from the receive field cannot be priced.
                <GreyCard style={{ textAlign: 'center' }}>
                  <Text mb="4px">
                    {TranslateString(1212, 'Enter an amount in the From field to get a quote.')}
                  </Text>
                </GreyCard>
              ) : noRoute && userHasSpecifiedInputOutput ? (
                <GreyCard style={{ textAlign: 'center' }}>
                  <Text mb="4px">{TranslateString(1194, 'Insufficient liquidity for this trade.')}</Text>
                </GreyCard>
              ) : showApproveFlow ? (
                <RowBetween>
                  <Button
                    onClick={approveCallback}
                    disabled={approval !== ApprovalState.NOT_APPROVED || approvalSubmitted}
                    style={{ width: '48%' }}
                    variant={approval === ApprovalState.APPROVED ? 'success' : 'primary'}
                  >
                    {approval === ApprovalState.PENDING ? (
                      <AutoRow gap="6px" justify="center">
                        Approving <Loader stroke="white" />
                      </AutoRow>
                    ) : approvalSubmitted && approval === ApprovalState.APPROVED ? (
                      'Approved'
                    ) : (
                      `Approve ${currencies[Field.INPUT]?.symbol}`
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      if (isExpertMode) {
                        handleSwap()
                      } else {
                        setAcceptedQuoteOutput(currentQuoteOutput)
                        setSwapState({
                          tradeToConfirm: trade,
                          attemptingTxn: false,
                          swapErrorMessage: undefined,
                          showConfirm: true,
                          txHash: undefined,
                        })
                      }
                    }}
                    style={{ width: '48%' }}
                    id="swap-button"
                    disabled={
                      !isValid ||
                      approval !== ApprovalState.APPROVED ||
                      (priceImpactSeverity > 3 && !isExpertMode) ||
                      (isExpertMode && attemptingTxn)
                    }
                    variant={isValid && priceImpactSeverity > 2 ? 'danger' : 'primary'}
                  >
                    {isExpertMode && attemptingTxn ? (
                      <AutoRow gap="6px" justify="center">
                        Confirming <Loader stroke="white" />
                      </AutoRow>
                    ) : priceImpactSeverity > 3 && !isExpertMode ? (
                      `Price Impact High`
                    ) : (
                      `Swap${priceImpactSeverity > 2 ? ' Anyway' : ''}`
                    )}
                  </Button>
                </RowBetween>
              ) : (
                <Button
                  onClick={() => {
                    if (isExpertMode) {
                      handleSwap()
                    } else {
                      setAcceptedQuoteOutput(currentQuoteOutput)
                      setSwapState({
                        tradeToConfirm: trade,
                        attemptingTxn: false,
                        swapErrorMessage: undefined,
                        showConfirm: true,
                        txHash: undefined,
                      })
                    }
                  }}
                  id="swap-button"
                  disabled={
                    !isValid ||
                    (priceImpactSeverity > 3 && !isExpertMode) ||
                    !!swapCallbackError ||
                    (isExpertMode && attemptingTxn)
                  }
                  variant={isValid && priceImpactSeverity > 2 && !swapCallbackError ? 'danger' : 'primary'}
                  width="100%"
                >
                  {isExpertMode && attemptingTxn ? (
                    <AutoRow gap="6px" justify="center">
                      Confirming <Loader stroke="white" />
                    </AutoRow>
                  ) : (
                    swapInputError ||
                    (priceImpactSeverity > 3 && !isExpertMode
                      ? `Price Impact Too High`
                      : `Swap${priceImpactSeverity > 2 ? ' Anyway' : ''}`)
                  )}
                </Button>
              )}
              {showApproveFlow && <ProgressSteps steps={[approval === ApprovalState.APPROVED]} />}
              {isExpertMode && swapErrorMessage ? <SwapCallbackError error={swapErrorMessage} /> : null}
            </BottomGrouping>
          </CardBody>
        </Wrapper>
      </AppBody>
      <AdvancedSwapDetailsDropdown trade={trade} />
    </>
  )
}

export default Swap
